import { Injectable } from '@angular/core';
import { Client, IMessage, Stomp } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { AuthService } from '../auth/auth.service';
import { NotificationService } from '../notification/notification.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class WebsocketService {
  private stompClient: Client | null = null;
  private connected: boolean = false;

  constructor(
    private authService: AuthService,
    private notificationService: NotificationService
  ) { }

  connect(): void {
    const token = this.authService.getToken();

    if (!token) {
      console.warn('WebSocket: Token não encontrado, conexão não iniciada');
      return;
    }

    // console.log('WebSocket: Iniciando conexão...');

    // Configurar o cliente STOMP
    this.stompClient = new Client({
      // Usar SockJS como transporte com token no query parameter
      webSocketFactory: () =>
        new SockJS(
          `${environment.serverUrl}/ws-aviso?token=${encodeURIComponent(token)}`
        ),

      // Headers de conexão (incluindo JWT)
      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },

      // Configurações de debug
      debug: (str: string) => {
        // console.log('WebSocket Debug:', str);
      },

      // Callback de conexão bem-sucedida
      onConnect: (frame) => {
        // console.log('WebSocket: Conectado com sucesso!', frame);
        this.connected = true;
        this.subscribeToAvisos();
      },

      // Callback de erro de conexão
      onStompError: (frame) => {
        console.error('WebSocket: Erro STOMP:', frame.headers['message']);
        console.error('WebSocket: Detalhes do erro:', frame.body);
        this.connected = false;
      },

      // Callback de desconexão
      onDisconnect: () => {
        // console.log('WebSocket: Desconectado');
        this.connected = false;
      },

      // Reconexão automática
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    // Ativar cliente
    this.stompClient.activate();
  }

  private subscribeToAvisos(): void {
    if (!this.stompClient || !this.connected) {
      console.warn(
        'WebSocket: Cliente não conectado, não é possível subscrever'
      );
      return;
    }

    // console.log('WebSocket: Subscrevendo em /user/queue/avisos...');

    // Subscrever nas notificações de avisos
    this.stompClient.subscribe('/user/queue/avisos', (message: IMessage) => {
      try {
        const avisoData = JSON.parse(message.body);
        // console.log(
        //   'WebSocket: Nova notificação de aviso recebida:',
        //   avisoData
        // );

        // Aqui você pode processar a notificação recebida
        this.handleAvisoNotification(avisoData);
      } catch (error) {
        console.error('WebSocket: Erro ao processar mensagem de aviso:', error);
      }
    });
  }

  private handleAvisoNotification(avisoData: any): void {
    // console.log('WebSocket: Processando notificação de aviso:', {
    //   id: avisoData.id,
    //   titulo: avisoData.titulo,
    //   mensagem: avisoData.mensagem,
    //   status: avisoData.status,
    //   dataAgendamento: avisoData.dataAgendamento,
    // });

    this.notificationService.handleNewAvisoReceived();

    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(`Novo aviso: ${avisoData.titulo}`, {
        body: avisoData.mensagem,
        icon: '/assets/svg/logos/saude_conectada_logo_min.svg'
      });
    }
  }

  disconnect(): void {
    if (this.stompClient) {
      // console.log('WebSocket: Desconectando...');
      this.stompClient.deactivate();
      this.connected = false;
    }
  }

  isConnected(): boolean {
    return this.connected;
  }

  // Método para subscrever em outras destinações no futuro
  subscribe(destination: string, callback: (message: IMessage) => void): void {
    if (!this.stompClient || !this.connected) {
      console.warn(
        `WebSocket: Não é possível subscrever em ${destination} - cliente não conectado`
      );
      return;
    }

    // console.log(`WebSocket: Subscrevendo em ${destination}...`);
    this.stompClient.subscribe(destination, callback);
  }

  // Método para enviar mensagens no futuro (se necessário)
  send(destination: string, body: any): void {
    if (!this.stompClient || !this.connected) {
      console.warn(
        `WebSocket: Não é possível enviar mensagem para ${destination} - cliente não conectado`
      );
      return;
    }

    this.stompClient.publish({
      destination: destination,
      body: JSON.stringify(body),
    });
  }
}
